import { useState } from "react";
import { useHistory } from "react-router-dom";
import { displayToast } from "../../../components/app/AppToast";
import SaveHeader from "../../../components/app/headers/SaveHeader";
import CustomAvatarUploader from "../../../components/profile/CustomAvatarUploader";
import DefaultAvatarSelector from "../../../components/profile/DefaultAvatarSelector";
import {
  useCurrentUser,
  reloadCurrentUser,
} from "../../../domains/user/hooks/useCurrentUser";
import { apiClient, toJsonFile } from "../../../services/api";
import { defaultAvatarImages } from "../../../domains/user/utils/pickUserImage";
import ScrollableLayout from "../../../components/layout/ScrollableLayout";
import { useCheckUserAccessRegistered } from "../../../domains/user/hooks/useCheckUserAccessRegistered";
import { useTranslation } from "react-i18next";
import { DefaultProfilePicture } from "../../../../../api/src/shared/domain/user";

export default function EditAvatarPage() {
  useCheckUserAccessRegistered();
  const history = useHistory();
  const { t } = useTranslation();
  const { currentUser } = useCurrentUser();
  const profilePicture = currentUser?.profilePicture;
  const [customAvatarFile, setCustomAvatarFile] = useState<string | null>(
    profilePicture?.image?.url ?? null
  );
  const [selectedDefaultAvatar, setSelectedDefaultAvatar] = useState<
    DefaultProfilePicture | ""
  >(profilePicture?.image?.url ? "" : (profilePicture?.name ?? ""));
  const [isEdited, setIsEdited] = useState(false);

  function handleChangeAvatarCustom(url: string) {
    setSelectedDefaultAvatar("");
    setCustomAvatarFile(url);
    setIsEdited(true);
  }

  function handleChangeAvatarDefault(slug: DefaultProfilePicture) {
    setSelectedDefaultAvatar(slug);
    setCustomAvatarFile(null);
    setIsEdited(true);
  }

  function handleClearAvatar() {
    if (customAvatarFile) {
      const defaultAvatars = Object.values(DefaultProfilePicture);
      const randomAvatar =
        defaultAvatars[Math.floor(Math.random() * defaultAvatars.length)];
      setCustomAvatarFile(null);
      if (randomAvatar !== undefined) {
        setSelectedDefaultAvatar(randomAvatar);
      }
      setIsEdited(true);
    }
  }

  async function handleSubmit() {
    try {
      if (selectedDefaultAvatar) {
        await apiClient.post("/webapp/private/users/me/avatar", {
          json: { pictureName: selectedDefaultAvatar, file: null },
        });
      } else if (customAvatarFile !== null) {
        const res = await fetch(customAvatarFile);
        const blob = await res.blob();
        const file = new File([blob], "avatar", { type: "image/jpeg" });
        await apiClient.post("/webapp/private/users/me/avatar", {
          json: { file: await toJsonFile(file), pictureName: null },
        });
      } else {
        return;
      }

      reloadCurrentUser();
      displayToast(t("Profile picture updated"), "success");
      history.goBack();
    } catch {
      displayToast(t("Something went wrong. Please try again."), "error");
    }
  }

  return (
    <ScrollableLayout
      header={
        <SaveHeader
          title={t("Edit avatar")}
          saveReady={isEdited}
          onSaveClick={handleSubmit}
        />
      }
    >
      <div className="pt-[80px] pb-[30px] px-[30px]">
        <CustomAvatarUploader
          avatarUrl={
            customAvatarFile
              ? customAvatarFile
              : selectedDefaultAvatar === ""
                ? ""
                : defaultAvatarImages[selectedDefaultAvatar].default
          }
          fullname={currentUser === null ? null : currentUser.fullname}
          buttonText={t("Upload profile picture")}
          onClear={customAvatarFile ? handleClearAvatar : undefined}
          onChange={handleChangeAvatarCustom}
        />

        <div className="my-[30px] mb-[20px] font-semibold text-[16px] text-primary-black leading-[30px]">
          {t("Select an avatar")}
        </div>

        <DefaultAvatarSelector
          selected={selectedDefaultAvatar}
          onChange={handleChangeAvatarDefault}
        />
      </div>
    </ScrollableLayout>
  );
}
